import { useQuery } from 'react-query';
import { Spin, Empty, Drawer, message } from 'design-system';
import React, { useRef, useMemo, useState, useEffect } from 'react';

import { Template } from 'src/services/template';
import { queryKeys } from 'src/constants/queryKeys';
import { getAllSubjects } from 'src/services/subject';

import DndProvider from './DndProvider';
import { 
  ExtendedSubject,
  getTemplateByType,
  setCurrentColumnSettings
} from './TableTemplates';

// 生成唯一实例ID，与编辑视图保持一致的策略
const generateInstanceId = () => `instance_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

interface TemplatePreviewDrawerProps {
  visible: boolean;
  template: Template | null;
  onClose: () => void;
}
export default function TemplatePreviewDrawer({ 
  visible, 
  template, 
  onClose 
}: TemplatePreviewDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<ExtendedSubject[]>([]);
  const [rowBackgroundColors, setRowBackgroundColors] = useState<Map<string, string>>(new Map());
  const [drawerTitle, setDrawerTitle] = useState('预览模板');
  const instanceId = useRef<string>(generateInstanceId());
  const [templateType, setTemplateType] = useState<string>('');
  
  const { data: subjectsData, isLoading: loadingSubjects, error: subjectsError } = useQuery({
    queryKey: [queryKeys.getAllSubjects],
    queryFn: () => getAllSubjects(),
    enabled: visible, 
    staleTime: 1000 * 60 * 60, 
    onError: (error: unknown) => {
      message.error('加载科目数据失败，预览可能不完整');
    }
  });
  
  const subjectMap = useMemo(() => {
    if (!subjectsData?.items) return new Map();
    const map = new Map();
    subjectsData.items.forEach(subject => {
      map.set(subject.id, subject);
    });
    return map;
  }, [subjectsData?.items]);
  
  // 当模板类型变化时更新instanceId，确保组件重新渲染
  useEffect(() => {
    if (template?.frequencyType && template.frequencyType !== templateType) {
      setTemplateType(template.frequencyType);
      instanceId.current = generateInstanceId();
    }
  }, [template, templateType]);
  
  // 确保在模板可见时设置列配置
  useEffect(() => {
    if (template && template.columnSettings) {
      setCurrentColumnSettings(template.columnSettings);
    }
    return () => {
      // 当Drawer关闭时重置列设置
      if (!visible) {
        setCurrentColumnSettings(null);
      }
    };
  }, [template, visible]);
  
  useEffect(() => {
    if (visible && template) {
      setLoading(true);
      setDrawerTitle(`预览模板：${template.name || '未命名模板'}`);
      const loadTemplateData = async () => {
        try {
          const templateSubjects: ExtendedSubject[] = [];
          const backgroundColors = new Map<string, string>();
          if (template.rowSettings && template.rowSettings.length > 0) {
            template.rowSettings.forEach(rowSetting => {
              const subjectDetail = subjectMap.get(rowSetting.subjectId);
              if (subjectDetail) {
                let parentName: string | undefined;
                let grandParentName: string | undefined;
                if (subjectDetail.parentId) {
                  const parentSubject = subjectMap.get(subjectDetail.parentId);
                  if (parentSubject) {
                    parentName = parentSubject.name;
                    if (parentSubject.parentId) {
                      const grandParentSubject = subjectMap.get(parentSubject.parentId);
                      if (grandParentSubject) {
                        grandParentName = grandParentSubject.name;
                      }
                    }
                  }
                }
                const subject: ExtendedSubject = {
                  ...subjectDetail,
                  id: rowSetting.subjectId,
                  name: subjectDetail.name,
                  parentId: subjectDetail.parentId,
                  parentName,
                  grandParentName,
                  createdAt: subjectDetail.createdAt || '',
                  updatedAt: subjectDetail.updatedAt || '',
                  assessmentDimension: subjectDetail.assessmentDimension ? {
                    id: subjectDetail.assessmentDimension.id,
                    name: subjectDetail.assessmentDimension.name,
                    createdAt: subjectDetail.assessmentDimension.createdAt || '',
                    updatedAt: subjectDetail.assessmentDimension.updatedAt || '',
                    enabled: typeof subjectDetail.assessmentDimension.enabled === 'boolean' ? subjectDetail.assessmentDimension.enabled : true
                  } : undefined
                };
                templateSubjects.push(subject);
              } else {
                const subject: ExtendedSubject = {
                  id: rowSetting.subjectId,
                  name: `科目 ${rowSetting.rowId}`, 
                  parentId: '',
                  createdAt: '',
                  updatedAt: '',
                };
                templateSubjects.push(subject);
              }
              if (rowSetting.backgroundColor) {
                backgroundColors.set(rowSetting.subjectId, rowSetting.backgroundColor);
              }
            });
          }
          setSubjects(templateSubjects);
          setRowBackgroundColors(backgroundColors);
        } catch (error) {
          // 错误处理
        } finally {
          setLoading(false);
        }
      };
      loadTemplateData();
    }
  }, [visible, template, subjectMap]);
  
  const renderTemplateTable = () => {
    if (!template) return <Empty description="无可用模板数据" />;
    if (loadingSubjects) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin tip="加载科目数据...">
            <div style={{ padding: '50px', minHeight: '200px', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }} />
          </Spin>
        </div>
      );
    }
    if (subjectsError) {
      return <Empty description="加载科目数据失败，无法正确预览模板" />;
    }
    
    // 使用与编辑视图相同的方式获取模板类型
    let templateType = '';
    
    // 首先判断是否是KPI类型
    if (template.type === 'KPI' || template.type === '核心指标年度计划表' || template.reportType === 'KPI') {
      templateType = 'kpi';
    } else {
      // 根据频率类型判断普通模板类型
      switch (template.frequencyType) {
        case 'Yearly':
          templateType = 'yearly';
          break;
        case 'Quarterly':
          templateType = 'quarterly';
          break;
        case 'Monthly':
          templateType = 'monthly';
          break;
        default:
          templateType = template.frequencyType.toLowerCase();
      }
    }
    
    // 使用getTemplateByType统一渲染逻辑，与编辑页面保持一致
    return getTemplateByType(
      templateType, 
      subjects, 
      null, // 预览模式不需要选中行
      rowBackgroundColors, 
      undefined, // 预览模式不需要行选择回调
      template.columnSettings,
      undefined, // 预览模式不需要删除功能
      true, // 设置为只读模式
      undefined, // 预览模式不需要表格数据变化回调
      template.frequencyType, // 传递频率类型
      template.reportType, // 传递报表类型
      (subjectsData?.items || []) as ExtendedSubject[] // 传递所有科目数据，用于KPI模板显示完整科目名称
    );
  };
  
  return (
    <Drawer
      title={drawerTitle}
      width={1200}
      placement="right"
      open={visible}
      onClose={onClose}
      maskClosable
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin tip="加载模板数据...">
            <div style={{ padding: '50px', minHeight: '200px', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }} />
          </Spin>
        </div>
      ) : (
        <div style={{ padding: '20px 0' }}>
          <DndProvider>
            <div key={instanceId.current}>
              {renderTemplateTable()}
            </div>
          </DndProvider>
        </div>
      )}
    </Drawer>
  );
} 